<template>
    <div>
        <!-- List View -->
        <div v-if="isListView" data-test-view="manage-categories">
            <l-space :size="1" />

            <div v-show="rights.A_SKILL" class="title-row">
                <button
                    class="btn btn-primary btn-block btn-raised"
                    data-test-id="category-create"
                    @click="showNewCategory()"
                >
                    {{ $t(`skills.modal.btnCreate`) }}
                </button>
            </div>

            <l-space :size="3" />

            <div v-for="(category, index) in categories" :key="category._id" class="title">
                <div class="row" data-test-class="category" :data-test-index="index">
                    <div
                        class="col-md-8 title-row"
                        data-test-class="category-name"
                        :data-test-id="`category-name-${index}`"
                    >
                        {{ category.name }}
                    </div>
                    <div
                        v-show="!category.removeConfirmation && !category.categorySet"
                        class="col-md-4 title-row"
                    >
                        <button
                            style="margin: 0px"
                            class="btn btn-primary btn-sm pull-right"
                            data-test-class="category-remove"
                            :data-test-id="`category-remove-${index}`"
                            @click="toggleDeleteConfirmation({ category, show: true })"
                        >
                            <i class="fal fa-trash"></i>
                        </button>
                        <button
                            style="margin: 0px"
                            class="btn btn-primary btn-sm pull-right"
                            data-test-class="category-edit"
                            :data-test-id="`category-edit-${index}`"
                            @click="showDetails(category)"
                        >
                            <i class="fal fa-pencil"></i>
                        </button>
                    </div>
                    <div v-show="category.categorySet" class="col-md-4 title-row">
                        <i
                            v-b-popover.hover.top="{
                                content: $t(`skills.modal.labelDefaultCategory`),
                            }"
                            class="color-grey fal fa-info-circle info-margin"
                        ></i>
                    </div>
                    <div
                        v-show="category.removeConfirmation"
                        class="col-md-4 title-row"
                        data-test-view="category-remove-confirmation"
                    >
                        <button
                            class="btn btn-danger btn-sm"
                            data-test-class="category-remove-confirm"
                            :data-test-id="`category-remove-confirm-${index}`"
                            @click="deleteCategory(category)"
                        >
                            {{ $t(`skills.modal.btnConfirmRemoveCategory`) }}
                        </button>
                        <button
                            class="btn btn-primary btn-sm"
                            data-test-class="category-remove-cancel"
                            :data-test-id="`category-remove-cancel-${index}`"
                            @click="toggleDeleteConfirmation({ category, show: false })"
                        >
                            {{ $t(`skills.modal.btnCancelRemoveCategory`) }}
                        </button>
                    </div>
                </div>

                <hr v-if="index !== categories.length - 1" />
            </div>

            <l-space :size="2" />
        </div>
        <!--/List View -->

        <!-- Single Category -->
        <div v-else data-test-view="category-upsert">
            <div class="title-row">
                <span
                    class="btn-fake color-primary clickable"
                    data-test-id="back"
                    @click="goBacktoList"
                >
                    <i class="fa fa-caret-left r-default-margin-s"></i> &nbsp;
                    {{ $t(`skills.modal.btnBackToList`) }}
                </span>
            </div>

            <hr />

            <l-space :size="1" />

            <l-input-text
                ref="input-name"
                v-model="category.name"
                :label="$t(`skills.modal.labelCategoryName`)"
                :optional="false"
                data-test-id="category-name"
            />

            <button
                class="btn btn-primary btn-block btn-raised"
                data-test-id="category-upsert"
                @click="category._id ? updateCategory(category) : createCategory(category)"
            >
                <span v-if="category._id">{{ $t(`skills.modal.btnUpdate`) }}</span>
                <span v-else>{{ $t(`skills.modal.btnCreate`) }}</span>
            </button>
        </div>
        <!--/Single Category -->
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import LInputText from "@/components/LInputText";
import LSpace from "@/components/LSpace";

export default {
    components: {
        LInputText,
        LSpace,
    },
    props: {
        id: {
            type: String,
            required: true,
        },
        close: {
            type: Function,
            required: true, // from router-view
        },
    },
    data() {
        return {
            category: null,
        };
    },
    computed: {
        ...mapGetters({
            rights: "users/current/rights",
            categories: "skills/getCategories",
            getCategoryDetails: "skills/getCategoryDetails",
        }),
        isListView() {
            return this.id === "list";
        },
    },
    watch: {
        $route: {
            immediate: true,
            handler(newRoute) {
                if (newRoute?.params?.id === "new") {
                    this.category = {};
                } else if (newRoute?.params?.id !== "list") {
                    this.category = this.getCategoryDetails(this.id);
                }
            },
        },
    },

    methods: {
        showNewCategory() {
            this.$router.push(`/skills/categories/new`);
        },
        showDetails(category) {
            this.$router.push(`/skills/categories/${category._id}`);
        },
        isValid() {
            const inputs = Object.keys(this.$refs)
                .map(key => (key.startsWith("input-") ? this.$refs[key] : null))
                .filter(Boolean);

            return inputs.reduce((a, c) => c.validate() && a, true);
        },
        createCategory(category) {
            if (!this.isValid()) {
                return;
            }

            this.$store
                .dispatch({
                    type: "skills/createCategory",
                    payload: { name: category.name },
                })
                .then(() => {
                    this.goBacktoList();
                });
        },
        updateCategory(category) {
            if (!this.isValid()) {
                return;
            }

            this.$store
                .dispatch({
                    type: "skills/updateCategory",
                    payload: { categoryId: category._id, name: category.name },
                })
                .then(() => {
                    this.goBacktoList();
                });
        },
        toggleDeleteConfirmation({ category, show }) {
            this.$set(category, "removeConfirmation", show);
            this.$forceUpdate();
        },
        deleteCategory(category) {
            this.$store.dispatch({
                type: "skills/deleteCategory",
                payload: { categoryId: category._id },
            });
        },

        goBacktoList() {
            this.$router.push(`/skills/categories/list`);
        },
    },
};
</script>

<style scoped>
.info-margin {
    margin-left: 15px;
}
</style>
